type TutorialTargetRect = Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left' | 'width' | 'height'>;

export interface TutorialCardPosition {
  top: number;
  left: number;
  placement: 'right' | 'left' | 'below' | 'above';
}

export function positionTutorialCard(
  target: TutorialTargetRect,
  card: { width: number; height: number },
  viewport: { width: number; height: number; left?: number; top?: number },
  gap = 16,
  margin = 16
): TutorialCardPosition {
  const left = viewport.left ?? 0;
  const top = viewport.top ?? 0;
  const position = positionWithinTutorialViewport({
    ...target,
    left: target.left - left,
    right: target.right - left,
    top: target.top - top,
    bottom: target.bottom - top,
    width: target.width,
    height: target.height
  }, card, viewport, gap, margin);
  return { ...position, left: position.left + left, top: position.top + top };
}

function positionWithinTutorialViewport(
  target: TutorialTargetRect,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
  gap = 16,
  margin = 16
): TutorialCardPosition {
  const clamp = (value: number, maximum: number) =>
    Math.min(Math.max(value, margin), Math.max(margin, maximum));
  const centeredTop = clamp(target.top + target.height / 2 - card.height / 2, viewport.height - card.height - margin);
  const centeredLeft = clamp(target.left + target.width / 2 - card.width / 2, viewport.width - card.width - margin);

  if (target.right + gap + card.width <= viewport.width - margin) {
    return { top: centeredTop, left: target.right + gap, placement: 'right' };
  }
  if (target.left - gap - card.width >= margin) {
    return { top: centeredTop, left: target.left - gap - card.width, placement: 'left' };
  }
  if (target.bottom + gap + card.height <= viewport.height - margin) {
    return { top: target.bottom + gap, left: centeredLeft, placement: 'below' };
  }
  if (target.top - gap - card.height >= margin) {
    return { top: target.top - gap - card.height, left: centeredLeft, placement: 'above' };
  }

  const horizontalSpace = Math.max(viewport.width - target.right, target.left);
  const verticalSpace = Math.max(viewport.height - target.bottom, target.top);
  if (horizontalSpace / card.width >= verticalSpace / card.height) {
    return viewport.width - target.right >= target.left
      ? { top: centeredTop, left: clamp(target.right + gap, viewport.width - card.width - margin), placement: 'right' }
      : { top: centeredTop, left: clamp(target.left - gap - card.width, viewport.width - card.width - margin), placement: 'left' };
  }
  return viewport.height - target.bottom >= target.top
    ? { top: clamp(target.bottom + gap, viewport.height - card.height - margin), left: centeredLeft, placement: 'below' }
    : { top: clamp(target.top - gap - card.height, viewport.height - card.height - margin), left: centeredLeft, placement: 'above' };
}
